import {
  ArrivalIcon,
  BikeStation,
  ComputedRoute,
  DepartureIcon,
  InstructionsList,
  Route,
  RouteService,
  Search,
  ShareIcon,
  WayPointRow,
  WayPointsList,
  facilityDistributionMinZoom,
  useLayers,
  useRouting,
  useSource,
} from '@geovelo-frontends/commons';
import { Download, MoreHoriz, SaveOutlined } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Chart } from 'chart.js';
import { Link, navigate } from 'gatsby';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AppContext, defaultSmallDeviceShowDetailsAction, mapPadding } from '../../context';
import DetailsLayout from '../../layouts/app-content/details';
import { TPageState } from '../../page-props';
import RoadTypes from '../../templates/ride-details/road-types';
import Button from '../button';
import { FavoriteIcon, FeedbackIcon, InfoIcon, PrintIcon } from '../icons';
import MapMarkers from '../map-markers';
import ShareDialog from '../share-dialog';

import ElevationsData from './elevations-data';
import PrintDialog from './print-dialog';
import RouteData from './route-data';
import SaveRouteDialog from './save-dialog';

import { LngLatBounds } from '!maplibre-gl';

const highlightSourceId = 'route-highlight';

function RoutingDetails({
  path,
  route,
  bikeStationsMap,
  downloading,
  onDownload,
  onReport,
}: {
  bikeStationsMap: { [key: number]: BikeStation };
  downloading: boolean;
  onDownload: () => void;
  onReport?: () => void;
  path: string;
  route: ComputedRoute | Route | null | undefined;
}): JSX.Element {
  const [facilityDistributionEnabled, enableFacilityDistribution] = useState(false);
  const [instructionsShowed, showInstructions] = useState(false);
  const [saveDialogOpen, openSaveDialog] = useState(false);
  const [printDialogOpen, openPrintDialog] = useState(false);
  const [shareDialogOpen, openShareDialog] = useState(false);
  const [elevationChart, setElevationChart] = useState<Chart>();
  const [moreActionsOpen, openMoreActions] = useState<boolean>(false);
  const [removingFromFavorites, setRemovingFromFavorites] = useState(false);
  const {
    map: { current: map, zoom },
    user: { current: currentUser, routes: userRoutes },
    actions: { setSmallDeviceShowDetailsAction, setUserRoutes },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const theme = useTheme();
  const {
    initialized: mapInitialized,
    init: initMap,
    updateWayPoints: updateWayPointsOnMap,
    showRoutes: showRoutesOnMap,
    destroy: destroyMap,
  } = useRouting(theme, map, new Search(), {}, { readOnly: true, hidePopup: true }, false);
  const {
    addGeoJSONSource: addHighlightSource,
    updateGeoJSONSource: updateHighlightSource,
    clearGeoJSONSource: clearHighlightSource,
  } = useSource(map, highlightSourceId);
  const { addCircleLayer } = useLayers(map);

  useEffect(() => {
    setSmallDeviceShowDetailsAction({ labelKey: 'commons.actions.see_details', Icon: InfoIcon });

    return () => {
      setSmallDeviceShowDetailsAction(defaultSmallDeviceShowDetailsAction);
    };
  }, []);

  useEffect(() => {
    if (map) {
      initMap();
      addHighlightSource();

      addCircleLayer('route-highlight', highlightSourceId, {
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
        'circle-color': '#fff',
        'circle-opacity': 0.5,
        'circle-stroke-color': '#326ac2',
      });
    }

    return () => {
      clearHighlightSource();
      destroyMap();
    };
  }, [map]);

  useEffect(() => {
    if (zoom) {
      if (facilityDistributionEnabled && zoom < facilityDistributionMinZoom)
        enableFacilityDistribution(false);
      else if (!facilityDistributionEnabled && zoom >= facilityDistributionMinZoom)
        enableFacilityDistribution(true);
    }
  }, [zoom]);

  useEffect(() => {
    if (mapInitialized && route) {
      showRoutesOnMap([route], 0, { enableFacilityDistribution: true });
    }
  }, [mapInitialized, route, facilityDistributionEnabled]);

  useEffect(() => {
    if (mapInitialized && route) {
      const { wayPoints, bounds } = route;

      updateWayPointsOnMap(wayPoints);

      if (map && bounds) {
        const { north, east, south, west } = bounds;

        map.fitBounds(new LngLatBounds({ lat: south, lng: west }, { lat: north, lng: east }), {
          padding: mapPadding,
        });
      }
    }
  }, [mapInitialized, route]);

  useEffect(() => {
    if (route === null) navigate(`/${currentLanguage}/route`);
  }, [route]);

  function handleHighlight(
    data: { sectionIndex: number; geometryIndex: number } | null,
    zoom = false,
  ) {
    if (route && data) {
      const { sectionIndex, geometryIndex } = data;
      const section = route.sections[sectionIndex];
      const coordinates = section.geometry.coordinates[geometryIndex];
      if (!coordinates) return;

      const [lng, lat] = coordinates;

      updateHighlightSource({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Point', coordinates }, properties: {} }],
      });

      if (zoom && map) {
        map.flyTo({ center: { lat, lng }, zoom: 17 });
      }
    } else {
      clearHighlightSource();
    }
  }

  function handleSaveRouteDialogClose(id?: number) {
    if (id) navigate(`/${currentLanguage}/saved-route/${id}`);
    else openSaveDialog(false);
  }

  async function handleRemoveFromFavorites() {
    if (!(route instanceof Route) || !userRoutes) return;

    setRemovingFromFavorites(true);

    try {
      await RouteService.remove(route.id);
      const index = userRoutes.findIndex(({ id }) => id === route.id);
      if (index !== undefined) {
        const newRoutes = [...userRoutes];
        newRoutes.splice(index, 1);

        setUserRoutes(newRoutes);
      }
    } catch (err) {
      console.error(err);
    }

    setRemovingFromFavorites(false);
    navigate(`/${currentLanguage}/route`, { replace: true });
  }

  if (route === null) return <></>;

  const wayPoints = route ? route.wayPoints : [undefined, undefined];

  return (
    <>
      <DetailsLayout
        disableContentPadding
        actions={
          route ? (
            <>
              <Button
                color="primary"
                disabled={downloading}
                onClick={onDownload}
                size="small"
                startIcon={<Download />}
                variant="contained"
              >
                GPX
              </Button>
              {route instanceof ComputedRoute ? (
                currentUser ? (
                  <Button
                    color="primary"
                    onClick={() => openSaveDialog(true)}
                    size="small"
                    startIcon={<SaveOutlined />}
                    variant="contained"
                  >
                    {t('commons.actions.save')}
                  </Button>
                ) : (
                  <Button<TPageState>
                    color="primary"
                    component={Link}
                    size="small"
                    state={{ prevPath: path }}
                    to={`/${currentLanguage}/sign-in`}
                    variant="contained"
                  >
                    {t('commons.actions.save')}
                  </Button>
                )
              ) : (
                <Button
                  disabled={!userRoutes || removingFromFavorites}
                  onClick={handleRemoveFromFavorites}
                  size="small"
                  startIcon={<FavoriteIcon color="primary" />}
                  variant="outlined"
                >
                  {t('commons.actions.remove_from_favorites')}
                </Button>
              )}
              <Box position="relative">
                <Button
                  color="primary"
                  onClick={() => openMoreActions(!moreActionsOpen)}
                  size="small"
                  sx={{ minWidth: 'auto', height: '36px' }}
                  variant={moreActionsOpen ? 'contained' : 'outlined'}
                >
                  <MoreHoriz color={moreActionsOpen ? 'inherit' : 'primary'} />
                </Button>
                {moreActionsOpen && (
                  <Box
                    bottom={46}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    left={0}
                    position="absolute"
                  >
                    <Tooltip placement="left" title={t('commons.actions.print')}>
                      <span>
                        <Button
                          color="primary"
                          onClick={() => {
                            openMoreActions(false);
                            openPrintDialog(true);
                          }}
                          size="small"
                          sx={{
                            bgcolor: '#fff',
                            minWidth: 'auto',
                            height: '36px',
                            '&:hover': { bgcolor: '#fff' },
                          }}
                          variant="outlined"
                        >
                          <PrintIcon />
                        </Button>
                      </span>
                    </Tooltip>
                    {route instanceof ComputedRoute && onReport && (
                      <Tooltip placement="left" title={t('geovelo.routing.actions.report')}>
                        <span>
                          <Button
                            color="primary"
                            onClick={() => {
                              openMoreActions(false);
                              onReport();
                            }}
                            size="small"
                            sx={{
                              bgcolor: '#fff',
                              minWidth: 'auto',
                              height: '36px',
                              '&:hover': { bgcolor: '#fff' },
                            }}
                            variant="outlined"
                          >
                            <FeedbackIcon />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {route instanceof ComputedRoute && (
                      <Tooltip placement="left" title={t('commons.actions.share')}>
                        <span>
                          <Button
                            color="primary"
                            onClick={() => {
                              openMoreActions(false);
                              openShareDialog(true);
                            }}
                            size="small"
                            sx={{
                              bgcolor: '#fff',
                              minWidth: 'auto',
                              height: '36px',
                              '&:hover': { bgcolor: '#fff' },
                            }}
                            variant="outlined"
                          >
                            <ShareIcon />
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <></>
          )
        }
        title={
          route instanceof Route
            ? route.description
            : t(`commons.routing.titles.${route?.labelKey}_long`)
        }
      >
        <StyledWayPointsList>
          <DepartureIcon sx={{ position: 'absolute', top: '10px', left: '24px' }} />
          <ArrivalIcon sx={{ position: 'absolute', bottom: '34px', left: '24px' }} />
          <WayPointsList disableGap readonly>
            {wayPoints.map((_, index) => (
              <Fragment key={index}>
                <WayPointRow readonly index={index} map={map} wayPoints={wayPoints} />
              </Fragment>
            ))}
          </WayPointsList>
        </StyledWayPointsList>
        <Wrapper>
          <RouteData selectedComputedRoute={route} />
          <ElevationsData
            onHighlight={handleHighlight}
            selectedComputedRoute={route}
            setChart={setElevationChart}
          />
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography fontSize="1.125rem" fontWeight={700}>
              {t('geovelo.rides.detail.road_types')}
            </Typography>
            <RoadTypes disableTitle route={route} />
          </Box>
          <InstructionsList
            bikeStationsMap={bikeStationsMap}
            instructionsShowed={instructionsShowed}
            onHighlight={handleHighlight}
            onInstructionsHidden={() => showInstructions(false)}
            onInstructionsShowed={() => showInstructions(true)}
            route={route}
            variant={true}
          />
        </Wrapper>
      </DetailsLayout>
      {route && route instanceof ComputedRoute && (
        <SaveRouteDialog
          computedRoute={route}
          onClose={handleSaveRouteDialogClose}
          open={saveDialogOpen}
        />
      )}
      {route && (
        <PrintDialog
          bikeStationsMap={bikeStationsMap}
          elevationChart={elevationChart}
          onClose={() => openPrintDialog(false)}
          onSave={() => {
            openPrintDialog(false);
            setTimeout(() => {
              if (currentUser) openSaveDialog(true);
              else {
                navigate(`/${currentLanguage}/sign-in`, { state: { prevPath: path } });
              }
            }, theme.transitions.duration.leavingScreen);
          }}
          open={printDialogOpen}
          route={route}
        />
      )}
      <ShareDialog
        onClose={() => openShareDialog(false)}
        open={shareDialogOpen}
        shareText={t('geovelo.routing.share_text') || ''}
        url={(typeof window !== 'undefined' && window.location.href) || null}
      />
      <MapMarkers computedRoute={route} path={path} />
    </>
  );
}

const Wrapper = styled.div`
  padding: 24px;

  > *:not(:first-child) {
    margin-top: 40px;
  }
`;

const StyledWayPointsList = styled.div`
  flex-grow: 1;
  padding: 0 28px 24px 28px;
  position: relative;
  border-bottom: 1px solid #dce1e4;

  &::before {
    border: dotted #ccc;
    border-width: 0 0 0 4px;
    bottom: 60px;
    content: '';
    left: 34px;
    position: absolute;
    top: 32px;
  }
`;

export default RoutingDetails;
